import { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Paper,
} from "@mui/material";
import TableStatusRow from "../../../components/TableStatusRow/TableStatusRow";

const LOGS_DEVICE_TABLE_COLUMN_COUNT = 6;

const formatActivityDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime()) || date.getFullYear() <= 1970) {
    return "N/A";
  }
  return date.toLocaleString();
};

const LogsDeviceTable = ({
  deviceList,
  isDeviceLoading,
  deviceFetchError,
  onRetry,
  onRowClick,
  totalRows,
  rowsPerPage,
  page,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const handleSort = (columnKey) => {
    if (sortColumn !== columnKey) {
      setSortColumn(columnKey);
      setSortDirection("asc");
    } else if (sortDirection === "asc") {
      setSortDirection("desc");
    } else {
      setSortColumn(null);
      setSortDirection("asc");
    }
  };

  const sortedRows = useMemo(() => {
    if (!deviceList || !deviceList.length || !sortColumn) return deviceList || [];
    return [...deviceList].sort((a, b) => {
      let av = a[sortColumn];
      let bv = b[sortColumn];
      if (sortColumn === "lastActivityLoggedDate") {
        av = av ? new Date(av).getTime() : 0;
        bv = bv ? new Date(bv).getTime() : 0;
      }
      if (typeof av === "string" || typeof bv === "string") {
        const cmp = String(av || "").localeCompare(String(bv || ""), undefined, { numeric: true, sensitivity: "base" });
        return sortDirection === "asc" ? cmp : -cmp;
      }
      return sortDirection === "asc" ? (av ?? 0) - (bv ?? 0) : (bv ?? 0) - (av ?? 0);
    });
  }, [deviceList, sortColumn, sortDirection]);

  return (
    <div className="logs_table_container">
      <TableContainer component={Paper}>
        <Table aria-label="device table">
          <TableHead>
            <TableRow>
              <TableCell style={{ width: "8%" }}>SL NO</TableCell>
              <TableCell style={{ width: "20%" }}>
                <TableSortLabel
                  active={sortColumn === "userName"}
                  direction={sortColumn === "userName" ? sortDirection : "asc"}
                  onClick={() => handleSort("userName")}
                >
                  User Name
                </TableSortLabel>
              </TableCell>
              <TableCell style={{ width: "15%" }}>
                <TableSortLabel
                  active={sortColumn === "deviceId"}
                  direction={sortColumn === "deviceId" ? sortDirection : "asc"}
                  onClick={() => handleSort("deviceId")}
                >
                  Device ID
                </TableSortLabel>
              </TableCell>
              <TableCell style={{ width: "22%" }}>
                <TableSortLabel
                  active={sortColumn === "deviceName"}
                  direction={sortColumn === "deviceName" ? sortDirection : "asc"}
                  onClick={() => handleSort("deviceName")}
                >
                  Device Name
                </TableSortLabel>
              </TableCell>
              <TableCell style={{ width: "15%" }}>
                <TableSortLabel
                  active={sortColumn === "numberOfSlots"}
                  direction={sortColumn === "numberOfSlots" ? sortDirection : "asc"}
                  onClick={() => handleSort("numberOfSlots")}
                >
                  Number of Slots
                </TableSortLabel>
              </TableCell>
              <TableCell style={{ width: "20%" }}>
                <TableSortLabel
                  active={sortColumn === "lastActivityLoggedDate"}
                  direction={sortColumn === "lastActivityLoggedDate" ? sortDirection : "asc"}
                  onClick={() => handleSort("lastActivityLoggedDate")}
                >
                  Last Activity Date
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows && sortedRows.length > 0 ? (
              sortedRows.map((device, index) => (
                <TableRow
                  key={device.deviceId || index}
                  onClick={() => onRowClick(device)}
                  sx={{
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#f8fafc",
                    },
                  }}
                >
                  <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell>{device.userName || "-"}</TableCell>
                  <TableCell>{device.deviceId || "-"}</TableCell>
                  <TableCell>
                    <span className="patient-name-link">{device.deviceName || "-"}</span>
                  </TableCell>
                  <TableCell>{device.numberOfSlots ?? "-"}</TableCell>
                  <TableCell>{formatActivityDate(device.lastActivityLoggedDate)}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableStatusRow
                colSpan={LOGS_DEVICE_TABLE_COLUMN_COUNT}
                loading={isDeviceLoading}
                error={deviceFetchError}
                onRetry={onRetry}
                emptyMessage="No devices available"
              />
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <div className="logs_pagination">
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </div>
    </div>
  );
};

export default LogsDeviceTable;
